/**
 * LandingPage - Página inicial pública do HeveaTalent
 * 
 * Extraído do App.js durante refatoração Fase 1
 * 
 * Dependências:
 * - useTranslation (traduções)
 * - LoginDialog, RegisterDialog (AuthDialogs)
 * - LanguageSelector
 * - Card components (shadcn/ui)
 * - Users icon (lucide-react)
 */

import React from "react";
import { Card, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Users, Scale, Shield, Sparkles, Link2, Award, TrendingUp } from "lucide-react";
import { useTranslation } from "../translations";
import LanguageSelector from "./LanguageSelector";
import { LoginDialog, RegisterDialog } from "./AuthDialogs";

const LandingPage = () => {
  const { t } = useTranslation();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-green-100 sticky top-0 z-50">
        <div className="container mx-auto px-3 sm:px-4 py-3 sm:py-4 flex justify-between items-center gap-2">
          <div className="flex items-center space-x-2 min-w-0 flex-1">
            <div className="hidden xs:flex sm:flex w-7 h-7 sm:w-8 sm:h-8 bg-green-600 rounded-lg items-center justify-center flex-shrink-0 max-[420px]:hidden">
              <Users className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            <h1 className="text-base sm:text-2xl font-bold text-green-800 truncate min-w-0">HeveaTalent</h1>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-4 flex-shrink-0 [&_button]:h-8 [&_button]:px-2.5 [&_button]:text-xs sm:[&_button]:h-9 sm:[&_button]:px-4 sm:[&_button]:text-sm">
            <a href="/proposito" className="hidden md:inline text-sm font-medium text-gray-600 hover:text-green-700 transition-colors">
              Propósito
            </a>
            <LanguageSelector />
            <LoginDialog />
            <RegisterDialog />
          </div>
        </div>
      </header>

      {/* SEÇÃO 1 - HERO */}
      <section className="py-16 md:py-24 px-4">
        <div className="container mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            {t('manageHeveiculture')}
            <span className="text-green-600 block mt-2">{t('findBestProfessionals')}</span>
          </h1>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            {t('completePlatform')}
          </p>
          
          {/* 6 Benefícios principais em linha */}
          <div className="flex flex-wrap justify-center gap-4 md:gap-6 mb-10 max-w-4xl mx-auto">
            <div className="flex items-center gap-2 text-gray-700">
              <span className="text-green-600">✅</span>
              <span className="text-sm md:text-base">{t('jobBoard')}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <span className="text-green-600">✅</span>
              <span className="text-sm md:text-base">{t('community')}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <span className="text-green-600">✅</span>
              <span className="text-sm md:text-base">{t('bleedingCalendar')}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <span className="text-green-600">✅</span>
              <span className="text-sm md:text-base">{t('teamManagement')}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <span className="text-green-600">✅</span>
              <span className="text-sm md:text-base">{t('marketplace')}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <span className="text-green-600">✅</span>
              <span className="text-sm md:text-base">{t('workerCertification')}</span>
            </div>
          </div>

          {/* Botões de ação */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <RegisterDialog variant="hero" />
            <a href="#beneficios" className="inline-flex items-center justify-center px-6 py-3 border-2 border-green-600 text-green-700 font-semibold rounded-lg hover:bg-green-50 transition-colors">
              {t('seeHowItWorks')}
            </a>
          </div>

          {/* Imagem única representativa */}
          <div className="max-w-4xl mx-auto">
            <div className="relative overflow-hidden rounded-2xl shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1679321750826-34c10afa2b7d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxydWJiZXIlMjBwbGFudGF0aW9ufGVufDB8fHx8MTc1NzI4OTc2MXww&ixlib=rb-4.1.0&q=85" 
                alt="Heveicultura profissional" 
                className="w-full h-64 md:h-80 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6 text-white text-left">
                <p className="text-lg md:text-xl font-semibold">{t('professionalsCadastrados')}</p>
                <p className="text-sm opacity-90">{t('connectingProducersWorkers')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAIXA DE SLOGAN */}
      <section data-testid="slogan-banner" className="py-12 md:py-16 px-4 bg-green-800">
        <div className="container mx-auto text-center max-w-3xl">
          <p className="text-2xl md:text-4xl font-bold text-white leading-snug">
            Cansado do mesmo resultado?
            <span className="block mt-2 text-green-300">Faça escolhas diferentes.</span>
          </p>
          <p className="mt-4 text-base md:text-lg text-green-100 italic">
            HeveaTalent — onde toda cadeia se conecta
          </p>
          <a href="/cursos" data-testid="landing-cursos-link" className="inline-block mt-5 bg-white/10 border border-white/30 text-white text-sm md:text-base px-5 py-2.5 rounded-full hover:bg-white/20 transition-colors">
            🎓 Cursos gratuitos de sangria com certificado SENAR — veja as datas →
          </a>
          <a href="/tutoriais" data-testid="landing-tutoriais-link" className="inline-block mt-3 md:mt-5 md:ml-3 bg-white/10 border border-white/30 text-white text-sm md:text-base px-5 py-2.5 rounded-full hover:bg-white/20 transition-colors">
            📺 Vídeos tutoriais: aprenda a usar o app em minutos →
          </a>
        </div>
      </section>

      {/* SEÇÃO 2 - BENEFÍCIOS (6 Cards Visuais) */}
      <section id="beneficios" className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('whyUseHeveaTalent')}</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {t('everythingYouNeed')}
            </p>
          </div>
          
          {/* 6 Cards de Benefícios */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {/* Card 1 - Calendário */}
            <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200 hover:shadow-lg transition-all duration-300">
              <CardHeader className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">🗓️</span>
                </div>
                <CardTitle className="text-green-800 text-lg">{t('dailyControl')}</CardTitle>
                <CardDescription className="text-gray-600">
                  {t('dailyControlDesc')}
                </CardDescription>
              </CardHeader>
            </Card>

            {/* Card 2 - Equipe */}
            <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200 hover:shadow-lg transition-all duration-300">
              <CardHeader className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">👷</span>
                </div>
                <CardTitle className="text-blue-800 text-lg">{t('evaluateTeam')}</CardTitle>
                <CardDescription className="text-gray-600">
                  {t('evaluateTeamDesc')}
                </CardDescription>
              </CardHeader>
            </Card>

            {/* Card 3 - Selos */}
            <Card className="bg-gradient-to-br from-yellow-50 to-amber-50 border-yellow-200 hover:shadow-lg transition-all duration-300">
              <CardHeader className="text-center">
                <div className="w-16 h-16 bg-yellow-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">🏅</span>
                </div>
                <CardTitle className="text-yellow-800 text-lg">{t('certifyTeam')}</CardTitle>
                <CardDescription className="text-gray-600">
                  {t('certifyTeamDesc')}
                </CardDescription>
              </CardHeader>
            </Card>

            {/* Card 4 - Marketplace */}
            <Card className="bg-gradient-to-br from-orange-50 to-red-50 border-orange-200 hover:shadow-lg transition-all duration-300">
              <CardHeader className="text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">🏪</span>
                </div>
                <CardTitle className="text-orange-800 text-lg">{t('buyAndSell')}</CardTitle>
                <CardDescription className="text-gray-600">
                  {t('buyAndSellDesc')}
                </CardDescription>
              </CardHeader>
            </Card>

            {/* Card 5 - Vagas */}
            <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200 hover:shadow-lg transition-all duration-300">
              <CardHeader className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">💼</span>
                </div>
                <CardTitle className="text-purple-800 text-lg">{t('findWork')}</CardTitle>
                <CardDescription className="text-gray-600">
                  {t('findWorkDesc')}
                </CardDescription>
              </CardHeader>
            </Card>

            {/* Card 6 - Comunidade */}
            <Card className="bg-gradient-to-br from-teal-50 to-cyan-50 border-teal-200 hover:shadow-lg transition-all duration-300">
              <CardHeader className="text-center">
                <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">👥</span>
                </div>
                <CardTitle className="text-teal-800 text-lg">{t('shareExperiences')}</CardTitle>
                <CardDescription className="text-gray-600">
                  {t('shareExperiencesDesc')}
                </CardDescription>
              </CardHeader>
            </Card>
            
            {/* Card 7 - Análise de Mercado */}
            <Card className="bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-200 hover:shadow-lg transition-all duration-300 sm:col-span-2 lg:col-span-3">
              <CardHeader className="text-center">
                <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">📊</span>
                </div>
                <CardTitle className="text-emerald-800 text-lg">{t('marketAnalysisTab')}</CardTitle>
                <CardDescription className="text-gray-600">
                  {t('marketAnalysisDesc')}
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* SEÇÃO - PROPÓSITO HEVEATALENT */}
      <section id="proposito" className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <p className="text-green-600 font-semibold text-sm tracking-widest uppercase mb-3">{t('purposeLabel')}</p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('purposeTitle')}
            </h2>
            <div className="w-20 h-1 bg-green-600 mx-auto rounded-full"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-16">
            <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                <TrendingUp className="w-6 h-6 text-green-700" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('purposeMission')}</h3>
              <p className="text-gray-600 leading-relaxed">{t('purposeMissionText')}</p>
            </div>
            <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
                <Award className="w-6 h-6 text-emerald-700" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('purposeVision')}</h3>
              <p className="text-gray-600 leading-relaxed">{t('purposeVisionText')}</p>
            </div>
          </div>

          <div className="max-w-5xl mx-auto">
            <p className="text-center text-gray-500 font-medium mb-8 text-sm tracking-wide uppercase">{t('purposeValuesTitle')}</p>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="relative bg-white rounded-2xl p-8 border-t-4 border-t-amber-500 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-14 h-14 bg-amber-50 rounded-2xl flex items-center justify-center mb-5">
                  <Scale className="w-7 h-7 text-amber-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{t('purposeJustice')}</h3>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li className="flex items-start gap-2"><span className="text-amber-500 mt-0.5">&#9679;</span>{t('purposeJustice1')}</li>
                  <li className="flex items-start gap-2"><span className="text-amber-500 mt-0.5">&#9679;</span>{t('purposeJustice2')}</li>
                  <li className="flex items-start gap-2"><span className="text-amber-500 mt-0.5">&#9679;</span>{t('purposeJustice3')}</li>
                </ul>
              </div>
              <div className="relative bg-white rounded-2xl p-8 border-t-4 border-t-blue-500 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center mb-5">
                  <Shield className="w-7 h-7 text-blue-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{t('purposeSecurity')}</h3>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li className="flex items-start gap-2"><span className="text-blue-500 mt-0.5">&#9679;</span>{t('purposeSecurity1')}</li>
                  <li className="flex items-start gap-2"><span className="text-blue-500 mt-0.5">&#9679;</span>{t('purposeSecurity2')}</li>
                  <li className="flex items-start gap-2"><span className="text-blue-500 mt-0.5">&#9679;</span>{t('purposeSecurity3')}</li>
                </ul>
              </div>
              <div className="relative bg-white rounded-2xl p-8 border-t-4 border-t-green-500 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-14 h-14 bg-green-50 rounded-2xl flex items-center justify-center mb-5">
                  <Sparkles className="w-7 h-7 text-green-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{t('purposeSimplicity')}</h3>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li className="flex items-start gap-2"><span className="text-green-500 mt-0.5">&#9679;</span>{t('purposeSimplicity1')}</li>
                  <li className="flex items-start gap-2"><span className="text-green-500 mt-0.5">&#9679;</span>{t('purposeSimplicity2')}</li>
                  <li className="flex items-start gap-2"><span className="text-green-500 mt-0.5">&#9679;</span>{t('purposeSimplicity3')}</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="text-center mt-16">
            <blockquote className="text-xl md:text-2xl text-gray-700 italic max-w-3xl mx-auto font-light">
              "{t('purposeQuote')}"
            </blockquote>
          </div>
        </div>
      </section>

      {/* SEÇÃO 3 - COMO FUNCIONA (3 Passos) */}
      <section className="py-16 bg-gradient-to-br from-green-600 to-emerald-700">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">{t('startIn3Steps')}</h2>
            <p className="text-green-100 max-w-2xl mx-auto">
              {t('freeToStart')}
            </p>
          </div>
          
          {/* 3 Passos */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {/* Passo 1 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <span className="text-2xl font-bold text-green-600">1</span>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">{t('createFreeAccount')}</h3>
              <p className="text-green-100 text-sm">
                {t('quickSignup')}
              </p>
            </div>

            {/* Passo 2 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <span className="text-2xl font-bold text-green-600">2</span>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">{t('registerProperty')}</h3>
              <p className="text-green-100 text-sm">
                {t('addFarms')}
              </p>
            </div>

            {/* Passo 3 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <span className="text-2xl font-bold text-green-600">3</span>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">{t('startUsing')}</h3>
              <p className="text-green-100 text-sm">
                {t('registerBleedings')}
              </p>
            </div>
          </div>

          {/* CTA Final */}
          <div className="text-center mt-12">
            <RegisterDialog variant="hero-white" />
            <p className="text-green-200 text-sm mt-4">
              ✓ {t('freeToStartBullet')} &nbsp; ✓ {t('noCreditCard')} &nbsp; ✓ {t('cancelAnytime')}
            </p>
          </div>
        </div>
      </section>

      {/* Footer institucional */}
      <footer className="py-10 bg-gray-900 text-gray-400">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-4">
            <p className="text-sm">© 2025-2026 HeveaTalent - {t('footerText')}</p>
            <div className="flex items-center gap-6 text-sm">
              <a href="/proposito" className="hover:text-green-400 transition-colors">{t('footerPurpose') || 'Propósito'}</a>
              <a href="/tutoriais" data-testid="footer-tutoriais-link" className="hover:text-green-400 transition-colors">Tutoriais</a>
              <a href="/termos" className="hover:text-green-400 transition-colors">{t('termsTitle') || 'Termos de Uso'}</a>
              <a href="/privacidade" className="hover:text-green-400 transition-colors">{t('privacyTitle') || 'Política de Privacidade'}</a>
            </div>
          </div>
          <p className="text-gray-500 text-xs text-center">{t('tripeBased')}</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
